import React from 'react';
import { Link } from 'react-router-dom';
import { urlFor } from '../sanity/sanityClient';
import { MdOutlineMessage } from 'react-icons/md';
import { SlArrowRight } from "react-icons/sl";
import 'react-toastify/dist/ReactToastify.css';

const MobileTraderbrand = ({
  products,
  updateProductStatus,
  deleteProduct,
  showToastNotification,
  canManageProduct,   // (product) => boolean, decided by the parent
  canEditProduct,     // (product) => boolean, owner only
  onEditProduct,      // (product) => void, opens the edit modal
  showAdminTools,     // true only for an admin on their own shop page
}) => {

  const buttonStyle = {
    padding: '10px 16px',
    color: 'white',
    border: 'none',
    cursor: 'pointer',
    borderRadius: '6px',
    fontSize: '14px',
    fontWeight: '500',
    flex: '1',
    minWidth: '100px',
    textAlign: 'center',
    textDecoration: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.3s ease'
  };

  const handleDeleteProduct = async (product) => {
    // Re-check here too; never trust that the button was only
    // rendered for permitted viewers.
    if (!canManageProduct?.(product)) {
      showToastNotification('You are not allowed to delete this product', 'error');
      return;
    }
    try {
      await deleteProduct(product._id);
    } catch (error) {
      showToastNotification('Failed to delete product', 'error');
    }
  };

  const handleUpdateStatus = async (product, newStatus) => {
    if (!canManageProduct?.(product)) {
      showToastNotification('You are not allowed to update this product', 'error');
      return;
    }
    try {
      await updateProductStatus(product._id, newStatus);
    } catch (error) {
      showToastNotification('Failed to update product status', 'error');
    }
  };

  return (
    <div
      className="itemList"
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '15px',
        padding: '10px'
      }}
    >
      {products.map((product) => {
        const manageable = !!canManageProduct?.(product);

        return (
          <div
            key={product._id}
            style={{
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              transition: 'transform 0.3s',
              padding: '15px',
              border: '1px solid #e0e0e0',
              borderRadius: '12px',
              position: 'relative',
              backgroundColor: '#fff',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
            }}
          >

            {/* IMAGE SECTION */}
            <div
              className="image-container"
              style={{
                width: '100%',
                position: 'relative',
              }}
            >

              {product.image && (
                <img
                  src={urlFor(product.image[0])}
                  alt={product.name}
                  style={{
                    width: '100%',
                    height: '200px',
                    objectFit: 'cover',
                    borderRadius: '8px'
                  }}
                />
              )}

              {/* MESSAGE ICON TOP RIGHT — admin on own shop only */}
              {showAdminTools && product.user && (
                <Link
                  to={`/chat/${product.user}`}
                  style={{
                    position: 'absolute',
                    top: '10px',
                    right: '10px',
                    width: '32px',
                    height: '32px',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: 'rgba(0,0,0,0.6)',
                    color: 'white',
                    backdropFilter: 'blur(6px)',
                    textDecoration: 'none'
                  }}
                >
                  <MdOutlineMessage size={16} /><SlArrowRight size={16} />
                </Link>
              )}

              {/* STATUS BADGE */}
              {product.status && (
                <span
                  style={{
                    position: 'absolute',
                    top: 12,
                    left: 12,
                    backgroundColor:
                      product.status === 'available'
                        ? 'rgba(34, 197, 94, 0.9)'
                        : 'rgba(239, 68, 68, 0.9)',
                    color: 'white',
                    padding: '6px 12px',
                    borderRadius: '6px',
                    fontSize: '12px',
                    fontWeight: 'bold',
                    backdropFilter: 'blur(4px)'
                  }}
                >
                  {product.status.toUpperCase()}
                </span>
              )}

              <h4 style={{
                textAlign: 'start',
                margin: '8px 0',
                fontSize: '16px',
                fontWeight: '600',
                color: '#1f2937',
                width: '100%'
              }}>
                {product.name}
              </h4>

            </div>

            {/* INFO */}
            <div style={{
              display: 'block',
              margin: '8px 0',
              width: '100%'
            }}>

              <p style={{
                color: '#dc2626',
                margin: '0',
                fontSize: '16px',
                fontWeight: 'bold'
              }}>
                {product.currency || 'TZS'}: <strong>{product.price?.toLocaleString()}/=</strong>
              </p>

              {product.details && (
                <p style={{
                  margin: '8px 0',
                  fontSize: '14px',
                  color: '#6b7280',
                  lineHeight: '1.4'
                }}>
                  {product.details}
                </p>
              )}

              {/* BUTTONS */}
              <div
                className="buttons"
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  justifyContent: 'space-between',
                  gap: '10px',
                  marginTop: '15px',
                  width: '100%'
                }}
              >
                {manageable ? (
                  <>
                    {canEditProduct?.(product) && (
                      <button
                        type="button"
                        onClick={() => onEditProduct?.(product)}
                        style={{ ...buttonStyle, backgroundColor: '#2563eb' }}
                      >
                        Edit
                      </button>
                    )}

                    <button
                      type="button"
                      onClick={() => handleDeleteProduct(product)}
                      style={{
                        ...buttonStyle,
                        backgroundColor: '#dc2626',
                      }}
                    >
                      Delete
                    </button>

                    <button
                      type="button"
                      onClick={() =>
                        handleUpdateStatus(
                          product,
                          product.status === 'available' ? 'soldout' : 'available'
                        )
                      }
                      style={{
                        ...buttonStyle,
                        backgroundColor: product.status === 'available' ? '#f59e0b' : '#10b981',
                      }}
                    >
                      {product.status === 'available' ? 'Mark Sold Out' : 'Mark Available'}
                    </button>
                  </>
                ) : (
                  <Link
                    to={`/chat/${product.user}`}
                    style={{
                      ...buttonStyle,
                      backgroundColor: '#2563eb',
                      width: '100%'
                    }}
                  >
                    Message Seller
                  </Link>
                )}
              </div>

            </div>
          </div>
        );
      })}
    </div>
  );
};

export default MobileTraderbrand;